import { type UseQueryResult, useQuery } from '@tanstack/react-query'
import {
  type RequestError,
  extractHttpError,
} from 'components/api/extractHttpError'
import { API_PREFIX } from 'config'
import ky from 'ky'
import qs from 'query-string'

import { logEvents } from '../../logEvents'

export type Conversation = {
  uid: string
  organizationUid: string
  user: {
    name: string
    email: string
  }
  team: string | null
  subject: string
  duration: number
  dateTime: string
  aiInsights: {
    customSummary: string | null
    keyTopic: string | null
    keySubTopic: string | null
    rootCause: string | null
  }
}

export type PreviousInteractionsFilter = {
  customers?: string[]
}

const groupByDate = (
  conversations: Conversation[],
): [string, Conversation[] | undefined][] => {
  return Object.entries(
    Object.groupBy(conversations, (c) => c.dateTime.split('T')[0]),
  )
}

export const usePreviousInteractions = (
  apiToken: string | null,
  filter: PreviousInteractionsFilter = {},
): UseQueryResult<[string, Conversation[] | undefined][], RequestError> =>
  useQuery({
    enabled: !!apiToken,
    queryKey: [apiToken, 'previous-interactions', 'conversations', filter],
    queryFn: async () => {
      try {
        return await ky
          .get(
            qs.stringifyUrl(
              {
                url: `${API_PREFIX}/co-pilot/v1/previous-interactions/conversations`,
                query: {
                  ...filter,
                  token: apiToken,
                },
              },
              { skipEmptyString: true, skipNull: true },
            ),
          )
          .json<Conversation[]>()
      } catch (error) {
        const e = await extractHttpError(error)
        logEvents({
          eventType: 'usePreviousInteractions error',
          level: 'error',
          name: e.name,
          message: e.message,
          ...e.cause,
        })

        throw e
      }
    },
    select: groupByDate,
  })
